import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "@/components/ui/library/card";
import {
  RadioGroup,
  RadioGroupItem,
} from "@/components/ui/library/radio-group";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Controller } from "react-hook-form";
import { FormField } from "@/components/ui/others/FormField";
import SortableFieldGrid, {
  type SortableFieldGridItem,
} from "../SortableFieldGrid";
import { useLeadViewFieldOrder } from "@/features/leads/hook/useLeadViewFieldOrder";
import PhoneFieldWithCountry from "@/components/common/inputs/PhoneFieldWithCountry";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import {
  useAllowCreatedAtEdit,
  useLeadFormLockedFields,
} from "@/features/leads/api/useLeadFormConfig";
import { SingleDatePicker } from "@/components/ui/library/single-date-picker";
import {
  composeCreatedAt,
  toLocalDayIso,
  todayLocalDayIso,
} from "@/features/leads/utils/leadCreatedAt";
import { useEditLeadPhoneCheck } from "@/features/leads/hooks/useEditLeadPhoneCheck";
import { Lock } from "lucide-react";
import {
  useRequiredFieldMark,
  type RequiredFieldMark,
} from "@/features/leads/requirements/requiredFieldMarks";
import { RequiredStar } from "@/features/leads/requirements/requiredFieldMarks.ui";

const INPUT_STYLES =
  "bg-[#fcfcfc] border-[var(--color-black-2)] h-[40px] rounded-[12px] px-[12px] text-[14px] text-[var(--color-black-8)] tracking-[-0.56px]";

const LOCKED_STYLES =
  "disabled:bg-[#f4f6f5] disabled:text-[#697571] disabled:cursor-not-allowed disabled:opacity-100";

/**
 * Label with a lock badge for fields the tenant has locked against editing.
 * The server rejects a locked change independently, so this is an affordance
 * (explaining WHY the input is dead), never the enforcement itself.
 */
function LockableLabel({
  label,
  locked,
  required,
}: {
  label: string;
  locked: boolean;
  /** Stage-requirement star (see `requiredFieldMarks.tsx`). */
  required?: RequiredFieldMark | null;
}) {
  if (!locked) {
    return (
      <>
        {label}
        <RequiredStar mark={required} />
      </>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5">
      {label}
      <RequiredStar mark={required} />
      <span
        title="Locked by your administrator — this field can't be changed."
        className="inline-flex items-center gap-1 text-[11px] font-normal text-[#697571]"
      >
        <Lock className="size-3" />
        Locked
      </span>
    </span>
  );
}

export default function BasicDetailsEditMode({
  embedded,
}: {
  embedded?: boolean;
}) {
  const { leadRegister, leadControl, lead } = useActiveDataMutation();
  const { isFieldLocked } = useLeadFormLockedFields();

  // Changing a lead's number hits the same tenant-wide uniqueness rule as
  // creating one, so warn at the field rather than on save. The hook probes
  // ONLY while the number is being changed — an untouched number is never
  // rejected by the server, so warning about it would be a lie that also
  // disabled Save.
  const phoneCheck = useEditLeadPhoneCheck();

  const lockedName = isFieldLocked("basic:name");
  const lockedEmail = isFieldLocked("basic:email");
  const lockedMobile = isFieldLocked("basic:mobile");
  const lockedWhatsapp = isFieldLocked("basic:whatsapp");
  const lockedGender = isFieldLocked("basic:gender");
  const lockedLocation = isFieldLocked("basic:location");

  // Stage-requirement stars, keyed by the Leads column the gate names.
  const requiredName = useRequiredFieldMark("field", "name");
  const requiredEmail = useRequiredFieldMark("field", "email");
  const requiredPhone = useRequiredFieldMark("field", "phone");
  const requiredWhatsapp = useRequiredFieldMark("field", "whatsapp");
  const requiredLocation = useRequiredFieldMark("field", "location");

  // Org-wide "Add/Edit Created At" provision. The form holds the lead's full
  // ISO stamp; picking a day keeps the original time-of-day so the change is
  // exactly the day the user chose (see `leadCreatedAt.ts`).
  const allowCreatedAtEdit = useAllowCreatedAtEdit();
  const originalCreatedAt = (lead as any)?.createdAt as string | undefined;

  // Tenant-wide field order (Super Admins drag the grips; see
  // useLeadViewFieldOrder). Keys are shared with the view mode so one drag
  // arranges both.
  const basicOrder = useLeadViewFieldOrder("basic");
  const fields: SortableFieldGridItem[] = basicOrder.sortByOrder(
    [
      {
        key: "name",
        node: (
          <FormField label={<LockableLabel label="Full Name" locked={lockedName} required={requiredName} />}>
            <Input
              type="text"
              {...leadRegister("name")}
              disabled={lockedName}
              className={`${INPUT_STYLES} ${LOCKED_STYLES}`}
              placeholder="Enter name"
            />
          </FormField>
        ),
      },
      {
        key: "email",
        node: (
          <FormField label={<LockableLabel label="Email" locked={lockedEmail} required={requiredEmail} />}>
            <Input
              type="email"
              {...leadRegister("email")}
              disabled={lockedEmail}
              className={`${INPUT_STYLES} ${LOCKED_STYLES}`}
              placeholder="Enter email"
            />
          </FormField>
        ),
      },
      {
        key: "mobile",
        node: (
          // `h-auto` so the duplicate warning below the input isn't clipped by
          // FormField's default fixed-height control wrapper.
          <FormField
            label={<LockableLabel label="Mobile" locked={lockedMobile} required={requiredPhone} />}
            controlWrapperClassName="h-auto"
          >
            <PhoneFieldWithCountry control={leadControl} disabled={lockedMobile} />
            {!lockedMobile && phoneCheck.isChecking && (
              <p className="text-xs text-[var(--color-text-gray)] mt-1">
                Checking number…
              </p>
            )}
            {!lockedMobile && !phoneCheck.isChecking && phoneCheck.blocksSave && (
              <p className="text-xs text-red-500 mt-1">{phoneCheck.message}</p>
            )}
          </FormField>
        ),
      },
      {
        key: "whatsapp",
        node: (
          // Same code picker as Mobile (owner, 2026-10-08: "on the WhatsApp we
          // need country code also"). `whatsappCountryCode` is form-only; the
          // context joins it into the one stored string on save.
          <FormField label={<LockableLabel label="WhatsApp" locked={lockedWhatsapp} required={requiredWhatsapp} />}>
            <PhoneFieldWithCountry
              control={leadControl}
              countryCodeName="whatsappCountryCode"
              phoneName="whatsapp"
              placeholder="WhatsApp number"
              disabled={lockedWhatsapp}
            />
          </FormField>
        ),
      },
      {
        key: "gender",
        node: (
          <FormField label={<LockableLabel label="Gender" locked={lockedGender} />}>
            <Controller
              control={leadControl}
              name="gender"
              render={({ field }) => (
                <div className="flex gap-[16px] items-start w-full">
                  <RadioGroup
                    value={field.value}
                    onValueChange={field.onChange}
                    disabled={lockedGender}
                    className={`flex gap-[16px] items-start ${
                      lockedGender ? "opacity-60 cursor-not-allowed" : ""
                    }`}
                  >
                    {["Male", "Female", "Other"].map((option) => (
                      <div
                        key={option}
                        className="flex gap-[8px] items-center px-[8px] py-[6px] cursor-pointer"
                      >
                        <RadioGroupItem
                          value={option}
                          id={`gender-${option.toLowerCase()}`}
                          disabled={lockedGender}
                          className="mt-0"
                        />
                        <Label
                          htmlFor={`gender-${option.toLowerCase()}`}
                          className="font-['Helvetica_Neue',sans-serif] font-normal leading-[1.35] not-italic relative shrink-0 text-[var(--color-black-8)] text-[14px] text-nowrap tracking-[-0.56px] cursor-pointer mb-0 pb-0"
                        >
                          {option}
                        </Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              )}
            />
          </FormField>
        ),
      },
      {
        key: "location",
        node: (
          <FormField label={<LockableLabel label="Location" locked={lockedLocation} required={requiredLocation} />}>
            <Input
              type="text"
              {...leadRegister("location")}
              disabled={lockedLocation}
              className={`${INPUT_STYLES} ${LOCKED_STYLES}`}
              placeholder="Enter location"
            />
          </FormField>
        ),
      },
      ...(allowCreatedAtEdit
        ? [
            {
              key: "createdAt",
              node: (
                <FormField label="Created At">
                  <Controller
                    control={leadControl}
                    name="createdAt"
                    render={({ field }) => (
                      <SingleDatePicker
                        date={toLocalDayIso(field.value)}
                        maxDate={todayLocalDayIso()}
                        onDateChange={(day) =>
                          field.onChange(composeCreatedAt(day, originalCreatedAt))
                        }
                      />
                    )}
                  />
                </FormField>
              ),
            },
          ]
        : []),
    ],
    (f) => f.key,
  );

  const content = (
    <SortableFieldGrid
      columns={2}
      items={fields}
      canReorder={basicOrder.canReorder}
      onReorder={basicOrder.save}
    />
  );

  if (embedded) {
    return <div className="px-3 pt-0.5 pb-2">{content}</div>;
  }

  return (
    <Card className="border-var(--color-black-1) rounded-[16px] gap-[16px] p-[16px] shadow-none">
      <CardHeader className="px-0 pb-0">
        <CardTitle className="text-[16px] font-medium text-black tracking-[-0.32px] leading-normal">
          Basic details
        </CardTitle>
      </CardHeader>
      <CardContent className="px-0 pt-0">{content}</CardContent>
    </Card>
  );
}
